import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import QRCode from 'qrcode';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { paymentLinkService, GenerateLinkRequest, GenerateLinkResponse, InvoiceDetails, InvoiceLineItem } from '../../services/paymentLinkService';
import { FIAT_CURRENCIES } from '../../constants/fiatCurrencies';
import { exchangeRateService, USD_MINIMUM } from '../../services/exchangeRateService';
import { getRoleDisplayLabel } from '../../utils/displayLabels';

interface CreatePaymentLinkModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: (paymentLink: string) => void;
  mode?: 'paymentLink' | 'invoice';
}

interface PaymentLinkData {
  fiatCurrency: string;
  fiatAmount: string;
  userId: string;
  payerCountryCode: string;
  payerSubdivisionCode: string;
}

interface ComposerValidationErrors {
  fiatAmount?: string;
  userId?: string;
  payerCountryCode?: string;
  payerSubdivisionCode?: string;
  invoiceNumber?: string;
  issueDate?: string;
  dueDate?: string;
  issuerCompanyName?: string;
  payingCompanyName?: string;
  invoiceItems?: string;
  invoiceTotal?: string;
  itemNames: Record<number, string>;
  itemQuantities: Record<number, string>;
  itemUnitPrices: Record<number, string>;
}

const createEmptyValidationErrors = (): ComposerValidationErrors => ({
  itemNames: {},
  itemQuantities: {},
  itemUnitPrices: {},
});

const resolvePaymentPageUrl = (link?: string | null): string => {
  if (!link) {
    return '';
  }

  if (typeof window === 'undefined') {
    return link;
  }

  try {
    return new URL(link, window.location.origin).toString();
  } catch {
    return link;
  }
};

const createDefaultInvoiceItem = (): InvoiceLineItem => ({
  itemName: '',
  quantity: 1,
  unitPrice: 0,
  amount: 0,
});

const roundCurrency = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const buildInvoiceTaxLabel = (taxType?: string | null, taxPercent?: number | null): string => {
  if (!taxType || !taxPercent || taxPercent <= 0) {
    return '';
  }

  return `${taxType} ${taxPercent}%`;
};

const buildInvoiceServiceDescription = (items: InvoiceLineItem[]): string => {
  const names = items
    .map(item => item.itemName.trim())
    .filter(Boolean);

  return names.join(', ');
};

const calculateInvoiceLineAmount = (item: InvoiceLineItem): number => {
  if (!item.quantity || !item.unitPrice) {
    return 0;
  }

  return roundCurrency(item.quantity * item.unitPrice);
};

const calculateInvoiceTotals = (invoice: InvoiceDetails) => {
  const items = invoice.items.map(item => ({
    ...item,
    amount: calculateInvoiceLineAmount(item),
  }));
  const subtotal = roundCurrency(items.reduce((sum, item) => sum + (item.amount || 0), 0));
  const taxAmount = invoice.taxType && invoice.taxPercent
    ? roundCurrency(subtotal * invoice.taxPercent / 100)
    : 0;
  const total = roundCurrency(subtotal + taxAmount);

  return { items, subtotal, taxAmount, total };
};

const buildDefaultInvoiceData = (): InvoiceDetails => {
  const issueDate = new Date();
  const dueDate = new Date(issueDate);
  dueDate.setDate(dueDate.getDate() + 7);

  return {
    invoiceNumber: `INV-${issueDate.toISOString().slice(0, 10).replace(/-/g, '')}-${Math.floor(Math.random() * 900 + 100)}`,
    issueDate: issueDate.toISOString().slice(0, 10),
    dueDate: dueDate.toISOString().slice(0, 10),
    taxOrVat: '',
    taxType: '',
    taxPercent: null,
    issuerCompanyName: '',
    issuerCompanyAddress: '',
    issuerCompanyNumber: '',
    issuerTaxOrVatNumber: '',
    payingCompanyName: '',
    payingCompanyAddress: '',
    payingCompanyNumber: '',
    payingTaxOrVatNumber: '',
    serviceDescription: '',
    items: [createDefaultInvoiceItem()],
    notes: '',
    subtotal: 0,
    taxAmount: 0,
    total: 0,
  };
};

const CreatePaymentLinkModal: React.FC<CreatePaymentLinkModalProps> = ({
  show,
  onHide,
  onSuccess,
  mode = 'paymentLink'
}) => {
  const isInvoiceMode = mode === 'invoice';
  const [formData, setFormData] = useState<PaymentLinkData>({
    fiatCurrency: 'USD',
    fiatAmount: '',
    userId: '',
    payerCountryCode: '',
    payerSubdivisionCode: '',
  });
  const [invoiceData, setInvoiceData] = useState<InvoiceDetails>(buildDefaultInvoiceData);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [paymentResult, setPaymentResult] = useState<GenerateLinkResponse | null>(null);
  const [showInvoicePreviewModal, setShowInvoicePreviewModal] = useState<boolean>(false);
  const [invoicePreviewQrCodeDataUrl, setInvoicePreviewQrCodeDataUrl] = useState<string | null>(null);
  const [currencySearchTerm, setCurrencySearchTerm] = useState<string>('');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState<boolean>(false);
  const [minimumAmount, setMinimumAmount] = useState<number>(USD_MINIMUM);
  const [minimumAmountLoading, setMinimumAmountLoading] = useState<boolean>(false);
  const [showOptionalInvoiceDetails, setShowOptionalInvoiceDetails] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const amountInputRef = useRef<HTMLInputElement | null>(null);
  const recipientFieldRef = useRef<HTMLSelectElement | HTMLInputElement | null>(null);
  const payerCountryRef = useRef<HTMLInputElement | null>(null);
  const payerSubdivisionRef = useRef<HTMLInputElement | null>(null);
  const invoiceNumberRef = useRef<HTMLInputElement | null>(null);
  const issueDateRef = useRef<HTMLInputElement | null>(null);
  const dueDateRef = useRef<HTMLInputElement | null>(null);
  const issuerCompanyNameRef = useRef<HTMLInputElement | null>(null);
  const payingCompanyNameRef = useRef<HTMLInputElement | null>(null);
  const invoiceItemNameRefs = useRef<Array<HTMLInputElement | null>>([]);

  const userRole = getCurrentUserRole();
  const currentUserId = getCurrentUserId();
  const requiresRecipientSelection = userRole === 'Admin' || userRole === 'SuperAgent';
  const invoiceTotals = useMemo(() => calculateInvoiceTotals(invoiceData), [invoiceData]);
  const invoiceTaxLabel = useMemo(
    () => buildInvoiceTaxLabel(invoiceData.taxType, invoiceData.taxPercent),
    [invoiceData.taxPercent, invoiceData.taxType]
  );
  const invoiceServiceDescription = useMemo(
    () => buildInvoiceServiceDescription(invoiceTotals.items),
    [invoiceTotals.items]
  );

  const resolveFallbackRecipient = useCallback((): User | null => {
    if (!currentUserId) {
      return null;
    }

    const parsedUserId = Number.parseInt(currentUserId, 10);
    if (!Number.isFinite(parsedUserId) || parsedUserId <= 0) {
      return null;
    }

    const fallbackRole: User['role'] = userRole === 'SuperAgent'
      ? 'SuperAgent'
      : userRole === 'Admin'
        ? 'Admin'
        : 'Agent';

    return {
      id: parsedUserId,
      name: 'Your account',
      email: '',
      phone: '',
      settlementWallet: '',
      usdtWallet: '',
      role: fallbackRole,
    };
  }, [currentUserId, userRole]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      if (!requiresRecipientSelection) {
        setUsers([]);
        return;
      }

      const currentUser = await dashboardService.getCurrentUser();
      let relatedUsers: User[] = [];

      try {
        relatedUsers = await dashboardService.getUsers();
      } catch (relatedUsersError) {
        console.warn('Error fetching related users for recipient selection:', relatedUsersError);
      }

      if (userRole === 'SuperAgent') {
        const agentUsers = relatedUsers.filter(user => user.role === 'Agent' && user.createdById === currentUser.id);
        const availableRecipients = [currentUser, ...agentUsers].filter(
          (user, index, array) => array.findIndex(candidate => candidate.id === user.id) === index
        );
        const fallbackRecipient = resolveFallbackRecipient();
        const safeRecipients = availableRecipients.length > 0
          ? availableRecipients
          : fallbackRecipient
            ? [fallbackRecipient]
            : [];
        setUsers(safeRecipients);

        setFormData(prev => ({
          ...prev,
          userId:
            prev.userId && safeRecipients.some(user => user.id.toString() === prev.userId)
              ? prev.userId
              : currentUserId || currentUser.id.toString(),
        }));

        return;
      }

      if (userRole === 'Admin') {
        const nonAdminUsers = relatedUsers.filter(user => user.role !== 'Admin' && user.id !== currentUser.id);
        const availableRecipients = [currentUser, ...nonAdminUsers].filter(
          (user, index, array) => array.findIndex(candidate => candidate.id === user.id) === index
        );
        const fallbackRecipient = resolveFallbackRecipient();
        const safeRecipients = availableRecipients.length > 0
          ? availableRecipients
          : fallbackRecipient
            ? [fallbackRecipient]
            : [];
        setUsers(safeRecipients);
        setFormData(prev => ({
          ...prev,
          userId:
            prev.userId && safeRecipients.some(user => user.id.toString() === prev.userId)
              ? prev.userId
              : currentUserId || currentUser.id.toString(),
        }));
      } else {
        setUsers([]);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      const fallbackRecipient = resolveFallbackRecipient();
      if (fallbackRecipient) {
        setUsers([fallbackRecipient]);
        setFormData(prev => ({
          ...prev,
          userId: prev.userId || fallbackRecipient.id.toString(),
        }));
        setError(null);
      } else {
        setUsers([]);
        setError('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUserId, requiresRecipientSelection, resolveFallbackRecipient, userRole]);

  useEffect(() => {
    if (show) {
      setSubmitAttempted(false);
      if (requiresRecipientSelection) {
        void fetchUsers();
      }

      if (!requiresRecipientSelection && currentUserId) {
        setFormData(prev => ({ ...prev, userId: prev.userId || currentUserId }));
      }

      if (isInvoiceMode) {
        setInvoiceData(buildDefaultInvoiceData());
      }

      setShowOptionalInvoiceDetails(false);
      setCurrencySearchTerm(formData.fiatCurrency);
    }
  }, [show, currentUserId, formData.fiatCurrency, isInvoiceMode, fetchUsers, requiresRecipientSelection]);

  useEffect(() => {
    if (!show || !isInvoiceMode) {
      return;
    }

    const nextAmount = invoiceTotals.total > 0 ? invoiceTotals.total.toFixed(2) : '';
    setFormData(prev => (
      prev.fiatAmount === nextAmount
        ? prev
        : { ...prev, fiatAmount: nextAmount }
    ));
  }, [invoiceTotals.total, isInvoiceMode, show]);

  useEffect(() => {
    const fetchMinimumAmount = async () => {
      setMinimumAmountLoading(true);
      try {
        let min: number;
        try {
          min = await paymentLinkService.getMinimumFiatAmount(formData.fiatCurrency);
        } catch {
          min = await exchangeRateService.getMinimumAmount(formData.fiatCurrency);
        }
        setMinimumAmount(min);
      } catch (error) {
        console.error('Error fetching minimum amount:', error);
        setMinimumAmount(USD_MINIMUM); // Fallback to USD minimum
      } finally {
        setMinimumAmountLoading(false);
      }
    };

    if (formData.fiatCurrency) {
      fetchMinimumAmount();
    }
  }, [formData.fiatCurrency]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setShowCurrencyDropdown(false);
      }
    };

    if (showCurrencyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCurrencyDropdown]);

  useEffect(() => {
    const walletAddress = paymentResult?.uniqueWalletAddress?.trim();
    if (!showInvoicePreviewModal || !walletAddress) {
      setInvoicePreviewQrCodeDataUrl(null);
      return;
    }

    let active = true;
    void QRCode.toDataURL(walletAddress, {
      width: 260,
      margin: 1,
      color: { dark: '#111827', light: '#FFFFFF' },
    })
      .then(url => {
        if (active) {
          setInvoicePreviewQrCodeDataUrl(url);
        }
      })
      .catch(() => {
        if (active) {
          setInvoicePreviewQrCodeDataUrl(null);
        }
      });

    return () => {
      active = false;
    };
  }, [paymentResult?.uniqueWalletAddress, showInvoicePreviewModal]);

  const handleInputChange = (field: keyof PaymentLinkData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleInvoiceChange = (field: keyof InvoiceDetails, value: string | number | null) => {
    setInvoiceData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleInvoiceItemChange = (index: number, field: keyof InvoiceLineItem, value: string) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        if (field === 'itemName') {
          return { ...item, itemName: value };
        }

        const numericValue = value === '' ? 0 : parseFloat(value);
        return {
          ...item,
          [field]: Number.isNaN(numericValue) ? 0 : numericValue,
        };
      }),
    }));
    setError(null);
  };

  const addInvoiceItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, createDefaultInvoiceItem()],
    }));
  };

  const removeInvoiceItem = (index: number) => {
    setInvoiceData(prev => ({
      ...prev,
      items: prev.items.length === 1
        ? [createDefaultInvoiceItem()]
        : prev.items.filter((_, itemIndex) => itemIndex !== index),
    }));
    setError(null);
  };

  // Filter currencies based on search term - show all if search is empty
  const filteredCurrencies = currencySearchTerm.trim() === '' 
    ? FIAT_CURRENCIES 
    : FIAT_CURRENCIES.filter(currency =>
        currency.toLowerCase().includes(currencySearchTerm.toLowerCase())
      );

  const handleCurrencySelect = async (currency: string) => {
    setFormData(prev => ({ ...prev, fiatCurrency: currency }));
    setCurrencySearchTerm(currency);
    setShowCurrencyDropdown(false);
    
    // Update minimum amount for new currency
    try {
      let min: number;
      try {
        min = await paymentLinkService.getMinimumFiatAmount(currency);
      } catch {
        min = await exchangeRateService.getMinimumAmount(currency);
      }
      setMinimumAmount(min);
    } catch (error) {
      console.error('Error fetching minimum amount:', error);
      setMinimumAmount(USD_MINIMUM); // Fallback to USD minimum
    }
  };

  const handleCurrencySearchChange = (value: string) => {
    setCurrencySearchTerm(value);
    setShowCurrencyDropdown(true);
  };

  const handleCurrencyInputFocus = () => {
    setShowCurrencyDropdown(true);
    setCurrencySearchTerm('');
  };

  const selectedUser = useMemo(
    () => users.find(user => user.id.toString() === formData.userId),
    [users, formData.userId]
  );
  const enteredAmount = parseFloat(formData.fiatAmount);
  const hasEnteredAmount = !Number.isNaN(enteredAmount);
  const isBelowMinimum = hasEnteredAmount && enteredAmount < minimumAmount;
  const validationErrors = useMemo<ComposerValidationErrors>(() => {
    const nextErrors = createEmptyValidationErrors();
    const normalizedPayerCountryCode = formData.payerCountryCode.trim().toUpperCase();
    const normalizedPayerSubdivisionCode = formData.payerSubdivisionCode.trim().toUpperCase();

    if (!formData.fiatAmount || Number.isNaN(enteredAmount) || enteredAmount < minimumAmount) {
      nextErrors.fiatAmount = `Enter an amount of at least ${exchangeRateService.formatMinimum(formData.fiatCurrency, minimumAmount)}.`;
    }

    if (normalizedPayerCountryCode.length > 0 && normalizedPayerCountryCode.length !== 2) {
      nextErrors.payerCountryCode = 'Use a 2-letter ISO country code like US, CA, or GB.';
    }

    if (normalizedPayerSubdivisionCode.length > 0 && normalizedPayerCountryCode.length !== 2) {
      nextErrors.payerCountryCode ??= 'Enter a 2-letter buyer country code before adding a province or state code.';
      nextErrors.payerSubdivisionCode = 'Add a country code first, then use a province or state code like ON or CA-BC.';
    } else if (normalizedPayerSubdivisionCode.length > 0 && normalizedPayerSubdivisionCode.length < 2) {
      nextErrors.payerSubdivisionCode = 'Use a 2 to 8 character province, state, or subdivision code like ON or CA-BC.';
    }

    if (!formData.userId) {
      nextErrors.userId = !requiresRecipientSelection
        ? 'Recipient account is still loading.'
        : isInvoiceMode
          ? 'Choose who should receive this invoice.'
          : 'Choose who should receive this payment page.';
    }

    if (isInvoiceMode) {
      if (!invoiceData.invoiceNumber.trim()) {
        nextErrors.invoiceNumber = 'Invoice number is required.';
      }

      if (!invoiceData.issueDate) {
        nextErrors.issueDate = 'Issue date is required.';
      }

      if (!invoiceData.dueDate) {
        nextErrors.dueDate = 'Due date is required.';
      }

      if (!invoiceData.issuerCompanyName.trim()) {
        nextErrors.issuerCompanyName = 'Your company name is required.';
      }

      if (!invoiceData.payingCompanyName.trim()) {
        nextErrors.payingCompanyName = 'Client company name is required.';
      }

      if (invoiceTotals.items.length === 0 || invoiceTotals.items.every(item => !item.itemName.trim())) {
        nextErrors.invoiceItems = 'Add at least one invoice item.';
      }

      invoiceTotals.items.forEach((item, index) => {
        if (!item.itemName.trim()) {
          nextErrors.itemNames[index] = 'Enter an item name.';
        }

        if (item.quantity <= 0) {
          nextErrors.itemQuantities[index] = 'Qty must be greater than 0.';
        }

        if (item.unitPrice < 0) {
          nextErrors.itemUnitPrices[index] = 'Price cannot be negative.';
        }
      });

      if (invoiceTotals.total <= 0) {
        nextErrors.invoiceTotal = 'Invoice total must be greater than zero.';
      }

      if (invoiceData.issueDate && invoiceData.dueDate && invoiceData.dueDate < invoiceData.issueDate) {
        nextErrors.dueDate = 'Due date must be on or after the issue date.';
      }
    }

    return nextErrors;
  }, [
    enteredAmount,
    formData.fiatAmount,
    formData.fiatCurrency,
    formData.payerCountryCode,
    formData.payerSubdivisionCode,
    formData.userId,
    invoiceData.dueDate,
    invoiceData.invoiceNumber,
    invoiceData.issueDate,
    invoiceData.issuerCompanyName,
    invoiceData.payingCompanyName,
    invoiceTotals.items,
    invoiceTotals.total,
    isInvoiceMode,
    minimumAmount,
    requiresRecipientSelection,
  ]);
  const firstValidationError = useMemo(() => {
    const itemLevelError = Object.values(validationErrors.itemNames)[0]
      || Object.values(validationErrors.itemQuantities)[0]
      || Object.values(validationErrors.itemUnitPrices)[0];

    return (
      validationErrors.fiatAmount ||
      validationErrors.payerCountryCode ||
      validationErrors.payerSubdivisionCode ||
      validationErrors.userId ||
      validationErrors.invoiceNumber ||
      validationErrors.issueDate ||
      validationErrors.dueDate ||
      validationErrors.issuerCompanyName ||
      validationErrors.payingCompanyName ||
      validationErrors.invoiceItems ||
      validationErrors.invoiceTotal ||
      itemLevelError ||
      null
    );
  }, [validationErrors]);
  const missingRequirements = useMemo(() => {
    const items: string[] = [];

    if (validationErrors.fiatAmount) {
      items.push(isInvoiceMode ? 'invoice total from line items' : 'amount');
    }

    if (validationErrors.userId) {
      items.push('recipient');
    }

    if (validationErrors.payerCountryCode) {
      items.push('buyer country code');
    }

    if (validationErrors.payerSubdivisionCode) {
      items.push('buyer province or state code');
    }

    if (isInvoiceMode) {
      if (validationErrors.invoiceNumber) {
        items.push('invoice number');
      }

      if (validationErrors.issueDate) {
        items.push('issue date');
      }

      if (validationErrors.dueDate) {
        items.push('due date');
      }

      if (validationErrors.issuerCompanyName) {
        items.push('your company name');
      }

      if (validationErrors.payingCompanyName) {
        items.push('client company name');
      }

      if (validationErrors.invoiceItems || Object.keys(validationErrors.itemNames).length > 0) {
        items.push('invoice items');
      }
    }

    return items;
  }, [isInvoiceMode, validationErrors]);
  useEffect(() => {
    if (!submitAttempted || !firstValidationError) {
      return;
    }

    const target =
      validationErrors.fiatAmount ? amountInputRef.current
        : validationErrors.payerCountryCode ? payerCountryRef.current
          : validationErrors.payerSubdivisionCode ? payerSubdivisionRef.current
          : validationErrors.userId ? recipientFieldRef.current
          : validationErrors.invoiceNumber ? invoiceNumberRef.current
              : validationErrors.issueDate ? issueDateRef.current
                : validationErrors.dueDate ? dueDateRef.current
                  : validationErrors.issuerCompanyName ? issuerCompanyNameRef.current
                    : validationErrors.payingCompanyName ? payingCompanyNameRef.current
                      : Object.keys(validationErrors.itemNames).length > 0
                        ? invoiceItemNameRefs.current[Number(Object.keys(validationErrors.itemNames)[0])] ?? null
                        : null;

    if (!target) {
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if ('focus' in target && typeof target.focus === 'function') {
      window.setTimeout(() => target.focus(), 120);
    }
  }, [firstValidationError, submitAttempted, validationErrors]);
  const validatedProviderSummary = paymentResult?.validatedProviders?.length
    ? 'Buyer-time routing enabled'
    : 'Direct crypto only';
  const recipientSummary = useMemo(() => {
    if (!requiresRecipientSelection) {
      return 'Your account';
    }

    if (selectedUser) {
      return `${selectedUser.name} (${getRoleDisplayLabel(selectedUser.role)})`;
    }

    return 'Choose an account';
  }, [requiresRecipientSelection, selectedUser]);
  const optionalInvoiceDetailsCount = useMemo(() => (
    [
      invoiceData.issuerCompanyAddress,
      invoiceData.issuerCompanyNumber,
      invoiceData.issuerTaxOrVatNumber,
      invoiceData.payingCompanyAddress,
      invoiceData.payingCompanyNumber,
      invoiceData.payingTaxOrVatNumber,
      invoiceData.taxType,
      invoiceData.taxPercent,
      invoiceData.notes,
    ].filter(value => value !== null && value !== undefined && String(value).trim() !== '').length
  ), [
    invoiceData.issuerCompanyAddress,
    invoiceData.issuerCompanyNumber,
    invoiceData.issuerTaxOrVatNumber,
    invoiceData.notes,
    invoiceData.payingCompanyAddress,
    invoiceData.payingCompanyNumber,
    invoiceData.payingTaxOrVatNumber,
    invoiceData.taxPercent,
    invoiceData.taxType,
  ]);
  const isSubmitDisabled = submitting || loading;
  const resultUrl = paymentResult
    ? resolvePaymentPageUrl(paymentResult.paymentPageUrl || paymentResult.paymentLinks[0])
    : '';
  const isInvoiceResult = Boolean(paymentResult?.isInvoice);
  const invoicePreviewNumber = paymentResult?.invoiceNumber?.trim() || invoiceData.invoiceNumber.trim() || 'Pending';
  const invoicePreviewCurrency = formData.fiatCurrency || 'USD';
  const invoicePreviewTotal = invoiceTotals.total > 0
    ? `${invoiceTotals.total.toFixed(2)} ${invoicePreviewCurrency}`
    : `${formData.fiatAmount || '0.00'} ${invoicePreviewCurrency}`;
  const invoicePreviewWalletAddress = paymentResult?.uniqueWalletAddress?.trim() || '';
  const invoiceEmailBody = resultUrl
    ? [
      `Please find our invoice powered by RizzPay at the link: ${resultUrl}`,
      '',
      'You can pay it directly with USDC (POL) by scanning the QR code or copying the payment wallet address, or by using more than 60 payment methods including credit cards, Apple/Google Pay, bank transfer, and many more.',
      '',
      'After successful completion of your payment, you will get a notification on the invoice page opened via the link.',
    ].join('\n')
    : '';
  const invoiceEmailSubject = paymentResult?.invoiceNumber
    ? `Invoice ${paymentResult.invoiceNumber} powered by RizzPay`
    : 'Invoice powered by RizzPay';
  const invoiceEmailHref = resultUrl
    ? `mailto:?subject=${encodeURIComponent(invoiceEmailSubject)}&body=${encodeURIComponent(invoiceEmailBody)}`
    : '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setSubmitting(true);
    setError(null);

    try {
      if (firstValidationError) {
        setError('Please fix the highlighted fields and try again.');
        return;
      }

      const amount = parseFloat(formData.fiatAmount);
      const normalizedInvoice = isInvoiceMode
        ? {
            ...invoiceData,
            issueDate: invoiceData.issueDate || null,
            dueDate: invoiceData.dueDate || null,
            taxOrVat: invoiceTaxLabel,
            serviceDescription: invoiceServiceDescription,
            items: invoiceTotals.items,
            subtotal: invoiceTotals.subtotal,
            taxAmount: invoiceTotals.taxAmount,
            total: invoiceTotals.total,
          }
        : undefined;

      const requestData: GenerateLinkRequest = {
        userId: parseInt(formData.userId, 10),
        fiatCurrency: formData.fiatCurrency,
        amount,
        payerCountryCode: formData.payerCountryCode || undefined,
        payerSubdivisionCode: formData.payerSubdivisionCode || undefined,
        invoice: normalizedInvoice,
      };

      const result = await paymentLinkService.generatePaymentLinks(requestData);
      setPaymentResult(result);
      setShowResult(true);
      
      const generatedLink = resolvePaymentPageUrl(result.paymentPageUrl || result.paymentLinks[0]);
      if (onSuccess && generatedLink) {
        try {
          onSuccess(generatedLink);
        } catch (callbackError) {
          console.warn('Payment link created successfully, but the success handler failed:', callbackError);
        }
      }
    } catch (err) {
      console.error(`Error creating ${isInvoiceMode ? 'invoice' : 'payment link'}:`, err);
      if (axios.isAxiosError(err)) {
        const responseData = err.response?.data as
          | { expandedError?: { technicalMessage?: string; message?: string }; error?: { message?: string } }
          | undefined;

        const detailedMessage =
          responseData?.expandedError?.technicalMessage ||
          responseData?.expandedError?.message ||
          responseData?.error?.message ||
          err.message;

        setError(detailedMessage || `Failed to ${isInvoiceMode ? 'issue invoice' : 'create payment link'}`);
      } else if (err instanceof Error) {
        setError(err.message || `Failed to ${isInvoiceMode ? 'issue invoice' : 'create payment link'}`);
      } else {
        setError(`Failed to ${isInvoiceMode ? 'issue invoice' : 'create payment link'}`);
      }
    } finally {
      setSubmitting(false);
    }
  };


  const handleClose = () => {
    setError(null);
    setSubmitAttempted(false);
    setShowResult(false);
    setShowInvoicePreviewModal(false);
    setInvoicePreviewQrCodeDataUrl(null);
    setShowOptionalInvoiceDetails(false);
    setPaymentResult(null);
    setFormData({
      fiatCurrency: 'USD',
      fiatAmount: '',
      userId: !requiresRecipientSelection ? currentUserId || '' : '',
      payerCountryCode: '',
      payerSubdivisionCode: '',
    });
    setInvoiceData(buildDefaultInvoiceData());
    onHide();
  };

  const handleBackToForm = () => {
    setShowResult(false);
    setShowInvoicePreviewModal(false);
    setInvoicePreviewQrCodeDataUrl(null);
    setPaymentResult(null);
    setError(null);
    setSubmitAttempted(false);
  };

  const copyToClipboard = (text: string) => {
    const clipboard = navigator?.clipboard;
    if (!clipboard?.writeText) {
      return;
    }

    clipboard.writeText(text).catch(() => {
    });
  };

  const buildPaymentLinkShareMessage = (url: string) => `Hey, follow this link to pay me: ${url}`;

  const handleShowInvoicePreview = (event?: React.MouseEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setShowInvoicePreviewModal(true);
  };


  return (
    <Modal
      show={show}
      onHide={handleClose}
      fullscreen
      dialogClassName="payment-composer__dialog"
      contentClassName="operator-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isInvoiceMode ? 'Issue Invoice' : 'Create Payment Link'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="payment-composer">
          {!showResult ? (
          <Form onSubmit={handleSubmit}>
            <div className="payment-composer__intro">
              <div className="payment-composer__eyebrow">{isInvoiceMode ? 'Invoice Composer' : 'Payment Link Composer'}</div>
              <div className="payment-composer__title">{isInvoiceMode ? 'Build an invoice that feels simple to send and easy to pay.' : 'Create a payment page without exposing the setup complexity.'}</div>
              <p className="payment-composer__copy">
                {isInvoiceMode
                  ? 'Set the currency, amount, recipient, and invoice details here. We will calculate totals for you and keep the payment surface aligned with the rest of the product.'
                  : 'Choose the currency, amount, and recipient here. Fiat providers are resolved later for each payer based on their location, currency, and live buyer-time availability.'}
              </p>
              <div className="payment-composer__requirements">
                <div className="payment-composer__requirement-card">
                  <div className="payment-composer__requirement-label">Required now</div>
                  <div className="payment-composer__requirement-chips">
                    <span className="payment-composer__requirement-chip">Currency and amount</span>
                    <span className="payment-composer__requirement-chip">Recipient</span>
                    <span className="payment-composer__requirement-chip">
                      {isInvoiceMode ? 'Invoice essentials' : 'Payer-facing routing'}
                    </span>
                  </div>
                </div>
                <div className="payment-composer__requirement-card">
                  <div className="payment-composer__requirement-label">Optional later</div>
                  <div className="payment-composer__requirement-copy">
                    {isInvoiceMode
                      ? 'Addresses, company numbers, tax, and notes can be added only when this invoice needs them.'
                      : 'The final fiat providers are chosen for each payer at checkout time, so you do not need to manage provider picks while creating the page.'}
                  </div>
                </div>
              </div>
              {submitAttempted && missingRequirements.length > 0 && (
                <div className="payment-composer__missing-note">
                  Still needed: {missingRequirements.join(', ')}.
                </div>
              )}
            </div>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <div className="payment-composer__section">
              <div className="payment-composer__section-heading">
                <div className="payment-composer__section-title">Basics</div>
                <span className="payment-composer__section-badge">Required</span>
              </div>
              <div className="payment-composer__section-copy">
                Start with the currency and amount. We will use this to prepare the payment page and let buyer-time routing decide the valid fiat options later.
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Fiat Currency</Form.Label>
                <div ref={currencyDropdownRef} className="payment-composer__currency-surface">
                  <Form.Control
                    type="text"
                    value={currencySearchTerm}
                    onChange={(e) => handleCurrencySearchChange(e.target.value)}
                    onFocus={handleCurrencyInputFocus}
                    placeholder="Search or choose currency..."
                    autoComplete="off"
                    aria-label="Fiat currency"
                    aria-haspopup="listbox"
                    aria-expanded={showCurrencyDropdown}
                  />
                  {showCurrencyDropdown && (
                    <div className="payment-composer__currency-menu">
                      {filteredCurrencies.length > 0 ? (
                        filteredCurrencies.map((currency) => (
                          <button
                            key={currency}
                            type="button"
                            onClick={() => handleCurrencySelect(currency)}
                            className="payment-composer__currency-option"
                          >
                            {currency}
                          </button>
                        ))
                      ) : (
                        <div className="payment-composer__provider-empty px-3 py-2">
                          No currencies match your search
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label>{isInvoiceMode ? 'Invoice Total' : 'Fiat Amount'}</Form.Label>
                <Form.Control
                  ref={amountInputRef}
                  type="number"
                  step="0.01"
                  min={minimumAmount}
                  value={formData.fiatAmount}
                  onChange={(e) => handleInputChange('fiatAmount', e.target.value)}
                  placeholder={minimumAmountLoading ? 'Loading...' : isInvoiceMode ? 'Calculated from invoice items' : `Enter amount (minimum ${exchangeRateService.formatMinimum(formData.fiatCurrency, minimumAmount)})`}
                  readOnly={isInvoiceMode}
                  isInvalid={submitAttempted && !isInvoiceMode && !!validationErrors.fiatAmount}
                />
                {!isInvoiceMode && (
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.fiatAmount}
                  </Form.Control.Feedback>
                )}
                <Form.Text style={isBelowMinimum ? { color: 'var(--app-blue)' } : undefined}>
                  {minimumAmountLoading
                    ? 'Checking the minimum amount for this currency...'
                    : isInvoiceMode
                      ? `The invoice total is calculated from the item lines and should stay at or above ${exchangeRateService.formatMinimum(formData.fiatCurrency, minimumAmount)} for the broadest buyer-time fiat coverage.`
                      : `A minimum of ${exchangeRateService.formatMinimum(formData.fiatCurrency, minimumAmount)} helps keep buyer-time fiat options available.`}
                </Form.Text>
              </Form.Group>

              </div>

            {requiresRecipientSelection && (
              <div className="payment-composer__section">
                <div className="payment-composer__section-heading">
                  <div className="payment-composer__section-title">Recipient</div>
                  <span className="payment-composer__section-badge">Required</span>
                </div>
                <div className="payment-composer__section-copy">
                  Choose which account should own this page and appear in the reporting that follows it.
                </div>
                <Form.Group className="mb-0">
                  <Form.Label>Select User</Form.Label>
                  {loading ? (
                    <div className="d-flex align-items-center">
                      <Spinner size="sm" className="me-2 app-spinner" />
                      <span className="operator-muted-text">Loading users...</span>
                    </div>
                  ) : (
                    <Form.Select
                      ref={recipientFieldRef as React.Ref<HTMLSelectElement>}
                      value={formData.userId}
                      onChange={(e) => handleInputChange('userId', e.target.value)}
                      isInvalid={submitAttempted && !!validationErrors.userId}
                    >
                      <option value="">Select a user</option>
                      {users.map((user, index) => (
                        <option key={user.id} value={user.id}>
                          {userRole === 'SuperAgent' && index === 0 ? 'Primary account - ' : ''}{user.name} ({getRoleDisplayLabel(user.role)})
                        </option>
                      ))}
                    </Form.Select>
                  )}
                  <Form.Control.Feedback type="invalid" style={{ display: submitAttempted && !!validationErrors.userId ? 'block' : 'none' }}>
                    {validationErrors.userId}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            )}

            {!requiresRecipientSelection && (
              <div className="payment-composer__section">
                <div className="payment-composer__section-heading">
                  <div className="payment-composer__section-title">Recipient</div>
                  <span className="payment-composer__section-badge">Required</span>
                </div>
                <div className="payment-composer__section-copy">
                  This page will stay attached to your account automatically.
                </div>
                <Form.Group className="mb-0">
                  <Form.Label>User</Form.Label>
                  <Form.Control
                    ref={recipientFieldRef as React.Ref<HTMLInputElement>}
                    type="text"
                    value={selectedUser ? `${selectedUser.name} (${getRoleDisplayLabel(selectedUser.role)})` : 'Your account'}
                    readOnly
                  />
                </Form.Group>
              </div>
            )}

            <div className="payment-composer__section">
              <div className="payment-composer__provider-toolbar">
                <div>
                  <div className="payment-composer__section-heading payment-composer__section-heading--compact">
                    <div className="payment-composer__section-title mb-1">Fiat Providers</div>
                    <span className="payment-composer__section-badge payment-composer__section-badge--optional">
                      Buyer-time
                    </span>
                  </div>
                  <div className="payment-composer__section-copy mb-0">
                    Over 60 fiat providers supporting more than 120 payment methods globally. Providers are presented to each payer based on location, fiat currency, and the strongest buyer-time approval path for that region.
                  </div>
                </div>
              </div>
            </div>

            {isInvoiceMode && (
              <div className="payment-composer__section payment-composer__invoice-shell">
                <div className="payment-composer__section-heading">
                  <div className="payment-composer__section-title">Invoice Details</div>
                  <span className="payment-composer__section-badge">Required</span>
                </div>
                <div className="payment-composer__section-copy">
                  Add the commercial details once here. The invoice total and payment experience will stay in sync automatically.
                </div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Invoice Number</Form.Label>
                      <Form.Control
                        ref={invoiceNumberRef}
                        value={invoiceData.invoiceNumber}
                        onChange={(e) => handleInvoiceChange('invoiceNumber', e.target.value)}
                        isInvalid={submitAttempted && !!validationErrors.invoiceNumber}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.invoiceNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Issue Date</Form.Label>
                      <Form.Control
                        ref={issueDateRef}
                        type="date"
                        value={invoiceData.issueDate || ''}
                        onChange={(e) => handleInvoiceChange('issueDate', e.target.value)}
                        isInvalid={submitAttempted && !!validationErrors.issueDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.issueDate}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-4">
                    <Form.Group>
                      <Form.Label>Due Date</Form.Label>
                      <Form.Control
                        ref={dueDateRef}
                        type="date"
                        value={invoiceData.dueDate || ''}
                        onChange={(e) => handleInvoiceChange('dueDate', e.target.value)}
                        isInvalid={submitAttempted && !!validationErrors.dueDate}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.dueDate}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Your Company Name</Form.Label>
                      <Form.Control
                        ref={issuerCompanyNameRef}
                        value={invoiceData.issuerCompanyName}
                        onChange={(e) => handleInvoiceChange('issuerCompanyName', e.target.value)}
                        isInvalid={submitAttempted && !!validationErrors.issuerCompanyName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.issuerCompanyName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Client Company Name</Form.Label>
                      <Form.Control
                        ref={payingCompanyNameRef}
                        value={invoiceData.payingCompanyName}
                        onChange={(e) => handleInvoiceChange('payingCompanyName', e.target.value)}
                        isInvalid={submitAttempted && !!validationErrors.payingCompanyName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.payingCompanyName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>

                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 mb-2">
                      <div className="payment-composer__subsection-label mb-0">
                        Invoice Details
                      </div>
                      <Button type="button" variant="outline-info" size="sm" onClick={addInvoiceItem}>
                        Add Item
                      </Button>
                    </div>
                    <div className="payment-composer__invoice-table">
                      <div className="row g-0 payment-composer__invoice-table-header">
                        <div className="col-md-5 p-2 border-end border-secondary">Item Name</div>
                        <div className="col-md-2 p-2 border-end border-secondary">Qty</div>
                        <div className="col-md-2 p-2 border-end border-secondary">Price</div>
                        <div className="col-md-2 p-2 border-end border-secondary">Amount</div>
                        <div className="col-md-1 p-2"> </div>
                      </div>
                      {invoiceTotals.items.map((item, index) => (
                        <div key={index} className="row g-0 payment-composer__invoice-row">
                          <div className="col-md-5 p-2 border-end border-secondary">
                            <Form.Control
                              ref={(element: HTMLInputElement | null) => {
                                invoiceItemNameRefs.current[index] = element;
                              }}
                              value={invoiceData.items[index]?.itemName || ''}
                              onChange={(e) => handleInvoiceItemChange(index, 'itemName', e.target.value)}
                              placeholder="Item name"
                              isInvalid={submitAttempted && !!validationErrors.itemNames[index]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.itemNames[index]}
                            </Form.Control.Feedback>
                          </div>
                          <div className="col-md-2 p-2 border-end border-secondary">
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0"
                              value={invoiceData.items[index]?.quantity || ''}
                              onChange={(e) => handleInvoiceItemChange(index, 'quantity', e.target.value)}
                              isInvalid={submitAttempted && !!validationErrors.itemQuantities[index]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.itemQuantities[index]}
                            </Form.Control.Feedback>
                          </div>
                          <div className="col-md-2 p-2 border-end border-secondary">
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0"
                              value={invoiceData.items[index]?.unitPrice || ''}
                              onChange={(e) => handleInvoiceItemChange(index, 'unitPrice', e.target.value)}
                              isInvalid={submitAttempted && !!validationErrors.itemUnitPrices[index]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.itemUnitPrices[index]}
                            </Form.Control.Feedback>
                          </div>
                          <div className="col-md-2 p-2 border-end border-secondary d-flex align-items-center payment-composer__invoice-value">
                            {item.amount?.toFixed(2) || '0.00'}
                          </div>
                          <div className="col-md-1 p-2 d-flex align-items-center justify-content-center">
                            <Button
                              type="button"
                              variant="outline-danger"
                              size="sm"
                              onClick={() => removeInvoiceItem(index)}
                              disabled={invoiceData.items.length === 1}
                              aria-label="Remove item"
                              title="Remove item"
                            >
                              x
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Form.Text>
                      Add one or more items. The invoice total is calculated automatically from these lines.
                    </Form.Text>
                    {submitAttempted && validationErrors.invoiceItems && (
                      <div className="text-danger small mt-2">
                        {validationErrors.invoiceItems}
                      </div>
                    )}
                  </div>

                  <div className="col-12">
                    <div className="payment-composer__provider-toolbar payment-composer__provider-toolbar--toggle">
                      <div>
                        <div className="payment-composer__subsection-label mb-0">
                          Optional invoice details
                        </div>
                        <div className="payment-composer__helper-note mt-2">
                          Add addresses, company numbers, tax, and notes only when this invoice needs them.
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline-light"
                        size="sm"
                        onClick={() => setShowOptionalInvoiceDetails((previous) => !previous)}
                      >
                        {showOptionalInvoiceDetails
                          ? 'Hide optional details'
                          : optionalInvoiceDetailsCount > 0
                            ? `Show optional details (${optionalInvoiceDetailsCount})`
                            : 'Add optional details'}
                      </Button>
                    </div>
                  </div>

                  {showOptionalInvoiceDetails && (
                    <>
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>Your Company Number</Form.Label>
                          <Form.Control
                            value={invoiceData.issuerCompanyNumber}
                            onChange={(e) => handleInvoiceChange('issuerCompanyNumber', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>Your Tax / VAT Number</Form.Label>
                          <Form.Control
                            value={invoiceData.issuerTaxOrVatNumber || ''}
                            onChange={(e) => handleInvoiceChange('issuerTaxOrVatNumber', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-12">
                        <Form.Group>
                          <Form.Label>Your Company Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={invoiceData.issuerCompanyAddress}
                            onChange={(e) => handleInvoiceChange('issuerCompanyAddress', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>Client Company Number</Form.Label>
                          <Form.Control
                            value={invoiceData.payingCompanyNumber}
                            onChange={(e) => handleInvoiceChange('payingCompanyNumber', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>Client Tax / VAT Number</Form.Label>
                          <Form.Control
                            value={invoiceData.payingTaxOrVatNumber || ''}
                            onChange={(e) => handleInvoiceChange('payingTaxOrVatNumber', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-12">
                        <Form.Group>
                          <Form.Label>Client Company Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={invoiceData.payingCompanyAddress}
                            onChange={(e) => handleInvoiceChange('payingCompanyAddress', e.target.value)}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>Add Tax / VAT</Form.Label>
                          <Form.Select
                            value={invoiceData.taxType || ''}
                            onChange={(e) => handleInvoiceChange('taxType', e.target.value)}
                          >
                            <option value="">No tax add-on</option>
                            <option value="VAT">VAT</option>
                            <option value="Tax">Tax</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>{invoiceData.taxType ? `${invoiceData.taxType} %` : 'Tax / VAT %'}</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={invoiceData.taxPercent || ''}
                            onChange={(e) => handleInvoiceChange('taxPercent', e.target.value === '' ? null : parseFloat(e.target.value))}
                            disabled={!invoiceData.taxType}
                            placeholder={invoiceData.taxType ? `Enter ${invoiceData.taxType.toLowerCase()} percentage` : 'Select Tax or VAT first'}
                          />
                        </Form.Group>
                      </div>

                      <div className="col-md-7">
                        <Form.Group>
                          <Form.Label>Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={invoiceData.notes || ''}
                            onChange={(e) => handleInvoiceChange('notes', e.target.value)}
                            placeholder="Optional payment or invoice notes"
                          />
                        </Form.Group>
                      </div>
                    </>
                  )}

                  <div className="col-md-5">
                    <div className="payment-composer__summary-shell">
                      <div className="payment-composer__summary-row">
                        <span>Subtotal</span>
                        <strong>{invoiceTotals.subtotal.toFixed(2)} {formData.fiatCurrency}</strong>
                      </div>
                      <div className="payment-composer__summary-row">
                        <span>{invoiceTaxLabel || 'Tax / VAT total'}</span>
                        <strong>{invoiceTotals.taxAmount.toFixed(2)} {formData.fiatCurrency}</strong>
                      </div>
                      <div className="payment-composer__summary-divider" />
                      <div className="payment-composer__summary-row payment-composer__summary-row--total">
                        <span>Total</span>
                        <strong>{invoiceTotals.total.toFixed(2)} {formData.fiatCurrency}</strong>
                      </div>
                    </div>
                    {submitAttempted && validationErrors.invoiceTotal && (
                      <div className="text-danger small mt-2">
                        {validationErrors.invoiceTotal}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="payment-composer__footer">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitDisabled}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" className="me-2 app-spinner" />
                    {isInvoiceMode ? 'Issuing...' : 'Creating...'}
                  </>
                ) : (
                  isInvoiceMode ? 'Issue Invoice' : 'Create Payment Link'
                )}
              </Button>
            </div>
          </Form>
        ) : paymentResult ? (
          <div className="payment-composer__result-shell">
            <div className="payment-composer__eyebrow">
              {paymentResult.isInvoice ? 'Invoice Ready' : 'Payment Page Ready'}
            </div>
            <div className="payment-composer__result-title">
              {paymentResult.isInvoice ? 'Invoice Page' : 'Payment Page'}
            </div>

            {paymentResult.invoiceNumber && (
              <Alert variant="info">
                Invoice Number: <strong>{paymentResult.invoiceNumber}</strong>
              </Alert>
            )}

            <div className="payment-composer__result-card">
              <div className="payment-composer__summary-row">
                <span>{paymentResult.isInvoice ? 'Invoice page' : 'Payment page'}</span>
                <strong>{resultUrl ? 'Ready' : 'Unavailable'}</strong>
              </div>
              <div className="payment-composer__result-link">
                {resultUrl}
              </div>
              <div className="payment-composer__result-actions">
                {isInvoiceResult ? (
                  <>
                    <Button
                      variant="outline-light"
                      onClick={() => copyToClipboard(resultUrl)}
                      title="Copy invoice link"
                      disabled={!resultUrl}
                    >
                      Copy invoice link
                    </Button>
                    <Button
                      variant="primary"
                      type="button"
                      disabled={!resultUrl}
                      onClick={handleShowInvoicePreview}
                    >
                      Preview invoice
                    </Button>
                    <Button
                      as="a"
                      href={invoiceEmailHref}
                      variant="outline-light"
                      disabled={!resultUrl}
                    >
                      Send invoice by email
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline-light"
                      onClick={() => copyToClipboard(resultUrl)}
                      title="Copy link"
                      disabled={!resultUrl}
                    >
                      Copy link
                    </Button>
                    <Button
                      variant="outline-light"
                      onClick={() => copyToClipboard(buildPaymentLinkShareMessage(resultUrl))}
                      title="Copy share text"
                      disabled={!resultUrl}
                    >
                      Copy share text
                    </Button>
                    <Button
                      as="a"
                      href={resultUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="primary"
                      disabled={!resultUrl}
                    >
                      Open page
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="payment-composer__summary-shell mt-3">
              <div className="payment-composer__summary-row">
                <span>Currency</span>
                <strong>{formData.fiatCurrency}</strong>
              </div>
              <div className="payment-composer__summary-row">
                <span>Amount</span>
                <strong>{formData.fiatAmount} {formData.fiatCurrency}</strong>
              </div>
              <div className="payment-composer__summary-row">
                <span>Recipient</span>
                <strong>{recipientSummary}</strong>
              </div>
                <div className="payment-composer__summary-row">
                  <span>Fiat routing</span>
                  <strong>{validatedProviderSummary}</strong>
                </div>
            </div>

            <div className="payment-composer__footer mt-3">
              <Button
                variant="secondary"
                onClick={handleBackToForm}
              >
                Back to Form
              </Button>
              <Button
                variant="primary"
                onClick={handleClose}
              >
                Close
              </Button>
            </div>
          </div>
        ) : null}

          {isInvoiceResult && (
            <Modal
              show={showInvoicePreviewModal}
              onHide={() => setShowInvoicePreviewModal(false)}
              centered
              size="lg"
              dialogClassName="payment-composer__invoice-preview-dialog"
            >
              <Modal.Header closeButton>
                <Modal.Title>Invoice preview</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="payment-composer__invoice-preview">
                  <div className="payment-composer__invoice-preview-head">
                    <div>
                      <div className="payment-composer__eyebrow">Invoice number</div>
                      <div className="payment-composer__invoice-preview-number">{invoicePreviewNumber}</div>
                    </div>
                    <div className="payment-composer__invoice-preview-total">{invoicePreviewTotal}</div>
                  </div>

                  <div className="payment-composer__invoice-preview-grid">
                    <div>
                      <span>Issue date</span>
                      <strong>{invoiceData.issueDate || 'Not set'}</strong>
                    </div>
                    <div>
                      <span>Due date</span>
                      <strong>{invoiceData.dueDate || 'Not set'}</strong>
                    </div>
                    <div>
                      <span>Issued by</span>
                      <strong>{invoiceData.issuerCompanyName || 'Not provided'}</strong>
                    </div>
                    <div>
                      <span>Issued to</span>
                      <strong>{invoiceData.payingCompanyName || 'Not provided'}</strong>
                    </div>
                  </div>

                  <div className="payment-composer__invoice-preview-wallet">
                    <div className="payment-composer__subsection-label mb-0">Destination wallet</div>
                    <p className="payment-composer__helper-note mt-2 mb-2">
                      Share this address or QR with the payer for direct wallet payment.
                    </p>
                    <div className="payment-composer__invoice-preview-wallet-grid">
                      {invoicePreviewQrCodeDataUrl && (
                        <img src={invoicePreviewQrCodeDataUrl} alt="Invoice payment QR code" />
                      )}
                      <div className="payment-composer__result-link">
                        {invoicePreviewWalletAddress || 'Wallet not available'}
                      </div>
                    </div>
                  </div>

                  <div className="payment-composer__invoice-preview-table-wrap">
                    <table className="payment-composer__invoice-preview-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Qty</th>
                          <th>Unit price</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceTotals.items.length === 0 ? (
                          <tr>
                            <td colSpan={4}>No invoice items.</td>
                          </tr>
                        ) : (
                          invoiceTotals.items.map((item, index) => (
                            <tr key={`${item.itemName}-${index}`}>
                              <td>{item.itemName || 'Invoice item'}</td>
                              <td>{item.quantity}</td>
                              <td>{item.unitPrice.toFixed(2)} {invoicePreviewCurrency}</td>
                              <td>{(item.amount || 0).toFixed(2)} {invoicePreviewCurrency}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="payment-composer__invoice-preview-totals">
                    <div><span>Subtotal</span><strong>{invoiceTotals.subtotal.toFixed(2)} {invoicePreviewCurrency}</strong></div>
                    <div><span>Tax</span><strong>{invoiceTotals.taxAmount.toFixed(2)} {invoicePreviewCurrency}</strong></div>
                    <div><span>Total</span><strong>{invoiceTotals.total.toFixed(2)} {invoicePreviewCurrency}</strong></div>
                  </div>

                  {invoiceData.notes?.trim() && (
                    <div className="payment-composer__helper-note">
                      <strong>Notes:</strong> {invoiceData.notes.trim()}
                    </div>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-light" onClick={() => setShowInvoicePreviewModal(false)}>
                  Close preview
                </Button>
              </Modal.Footer>
            </Modal>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default CreatePaymentLinkModal;
